/**
 * Where BoardFlight's own builds are downloaded from.
 *
 * A third origin, alongside the dashboard and the API: every build this project publishes —
 * the Unity package, the CLI bundle — lives in an R2 bucket
 * served at `download.boardflight.app`, rather than in the dashboard's static assets where it
 * used to live. The bucket is the archive, so an old version stays downloadable because it was
 * never removed, instead of because it was committed to git.
 *
 * That makes these fetches cross-origin, which is why the bucket carries a CORS rule naming
 * this origin (`scripts/r2-cors.json`). Without it every manifest below fails in the browser
 * while answering perfectly well in a terminal.
 *
 * Build with `VITE_DOWNLOAD_BASE_URL` to point a self-hosted deployment at its own bucket; the
 * default is the public one, which is also what the packers compile into the manifests
 * (see `scripts/download-url.mjs` — the two have to agree).
 */
const BASE = (import.meta.env.VITE_DOWNLOAD_BASE_URL ?? 'https://download.boardflight.app').replace(
  /\/+$/,
  '',
);

/** A URL under the download origin, e.g. `downloadUrl('unity/latest.json')`. */
export function downloadUrl(path: string): string {
  return `${BASE}/${path.replace(/^\/+/, '')}`;
}

/**
 * Read one of the published manifests.
 *
 * The content type is checked rather than only the status because neither end of this answers
 * a missing file with a clean failure the caller can read: the bucket returns its own error
 * document, and a request that lands on the dashboard instead gets `index.html` with a 200 from
 * the SPA fallback. Both parse as "not JSON", which is the honest thing to report.
 */
export async function manifest<T>(path: string): Promise<T> {
  const url = downloadUrl(path);

  // A CORS rejection reaches JavaScript as a bare `TypeError: Failed to fetch`, with no status
  // and no way to tell it apart from being offline — the one message that says nothing about
  // the one failure this module is most likely to have. The rule names origins exactly, so a
  // dashboard served from anywhere else (a preview deploy, 127.0.0.1 rather than localhost, a
  // LAN address) is refused while the same URL is healthy in a tab. Say which origin was
  // refused, because that is the value the fix needs.
  let response: Response;
  try {
    response = await fetch(url, { headers: { accept: 'application/json' } });
  } catch (failure) {
    throw new Error(
      `Could not reach ${url} from ${window.location.origin}: the browser refused the request ` +
        `before any answer came back. If ${url} loads in a tab, this origin is missing from the ` +
        `download bucket's CORS rule (scripts/r2-cors.json). Otherwise the network is down. ` +
        `(${failure instanceof Error ? failure.message : String(failure)})`,
    );
  }

  const contentType = response.headers.get('content-type') ?? '';
  if (!response.ok || !contentType.includes('json')) {
    throw new Error(
      `This download has not been published: ${url} answered ${response.status} ` +
        `with ${contentType || 'no content-type'} rather than JSON.`,
    );
  }
  return (await response.json()) as T;
}
